const AiInPat = () => {
  return (
    <>
      <p>
        <em>
          Psychedelic-assisted therapy is one of the most promising developments
          in mental health care in decades. It is also one of the most
          documentation-intensive. AI tools built for standard talk therapy do
          not account for the unique phases, experiences, and confidentiality
          demands of PAT. Here is what practitioners need to know about where AI
          fits &mdash; and where most tools fall short.
        </em>
      </p>

      <hr />

      <h2>The Documentation Challenge Unique to PAT</h2>

      <p>
        Most clinical documentation tools were designed for a straightforward
        model: a client arrives, you conduct a session, you write a note, and
        you repeat that cycle weekly until discharge. The tools assume a
        relatively uniform session structure. They assume the client is in an
        ordinary state of consciousness. They assume the treatment arc is
        linear.
      </p>

      <p>
        Psychedelic-assisted therapy breaks every one of those assumptions.
      </p>

      <p>
        PAT unfolds across at least three distinct phases, each with its own
        clinical demands and documentation requirements:
      </p>

      <ul>
        <li>
          <strong>Preparation.</strong> This phase involves thorough screening,
          medical history review, informed consent, psychoeducation about the
          substance and the process, intention setting, and the establishment of
          a strong therapeutic alliance. Documentation must capture readiness
          assessments, contraindication screening, and the client&rsquo;s
          expectations and fears. For practitioners working within clinical
          trials, additional protocol-specific documentation is required.
        </li>
        <li>
          <strong>Dosing or ceremony.</strong> This is the phase most unlike
          anything in conventional therapy. The client may be in a non-ordinary
          state of consciousness for several hours. They may report visual
          imagery, somatic sensations, emotional catharsis, mystical-type
          experiences, or encounters with trauma material that surfaces in
          non-verbal or symbolic form. Standard progress note formats &mdash;{" "}
          SOAP, BIRP, DAP &mdash; were not designed to capture this kind of
          clinical material. Yet the dosing session is often the most clinically
          significant event in the entire treatment arc, and it must be
          documented accurately.
        </li>
        <li>
          <strong>Integration.</strong> Integration sessions help the client
          make meaning of their experience and translate insights into lasting
          change. Documentation here needs to track how material from the dosing
          session is being processed, what new themes are emerging, and how the
          client&rsquo;s presenting concerns are shifting. Integration is not a
          single session &mdash; it can span weeks or months, and the
          documentation needs to reflect that continuity.
        </li>
      </ul>

      <p>
        On top of this structural complexity, PAT practitioners face heightened
        confidentiality concerns. Many psychedelic substances remain legally
        restricted or sit in regulatory grey areas. Clients may be
        participating in clinical trials with strict data governance
        requirements. Others may be accessing therapy through state or
        jurisdictional exemptions, compassionate access schemes, or in
        contexts where the legal status of the substance is ambiguous.
        Documentation that is imprecise, insecure, or stored in systems
        without adequate protections creates risk for both the client and the
        practitioner.
      </p>

      <p>
        The documentation burden that already weighs on therapists in conventional practice is significantly amplified in PAT. The volume
        is greater, the content is more complex, and the stakes around privacy
        are higher.
      </p>

      <h2>Why PAT Practitioners Need AI Documentation Support</h2>

      <p>
        The training pipeline for psychedelic-assisted therapy is accelerating.
        Programmes such as those offered by MAPS, Fluence, Naropa University,
        and various certificate providers are producing a growing cohort of
        clinicians who are trained in PAT modalities but may have limited
        experience with the specific documentation demands of this work.
        Training courses are often brief &mdash; ranging from weekend
        intensives to several-month programmes &mdash; and they
        understandably prioritise clinical skills over administrative workflow.
      </p>

      <p>
        The result is that practitioners move quickly from training into
        practice, where the documentation demands are immediate and
        substantial. A single client episode in PAT can generate:
      </p>

      <ul>
        <li>An intake assessment and screening report</li>
        <li>Medical clearance documentation</li>
        <li>Informed consent specific to the substance and protocol</li>
        <li>Preparation session notes (often two to four sessions)</li>
        <li>A dosing session record</li>
        <li>Integration session notes (often four to eight sessions or more)</li>
        <li>Outcome measures and treatment reviews</li>
        <li>A discharge summary</li>
      </ul>

      <p>
        Compare this to standard weekly psychotherapy, where the per-client
        documentation typically consists of an intake, a treatment plan, weekly
        progress notes, and a discharge summary. The PAT treatment arc
        generates significantly more documentation across more distinct
        document types, often compressed into a shorter overall timeframe.
      </p>

      <p>
        This is precisely the kind of challenge that well-designed AI tooling
        can address &mdash; not by replacing clinical judgement, but by
        reducing the mechanical effort of producing structured, high-quality
        documentation at each phase of the treatment arc.
      </p>

      <h2>What AI-Assisted Documentation Looks Like for PAT</h2>

      <p>
        The first thing to understand is that generic AI tools &mdash;
        ChatGPT with a blank prompt, a general-purpose note-taking app, or a
        transcription service designed for business meetings &mdash; are not
        fit for purpose in PAT. They lack clinical framing, they do not
        understand the structure of psychedelic therapy, and they have no
        awareness of the documentation standards or regulatory context that
        practitioners operate within.
      </p>

      <p>
        Purpose-built AI documentation support for PAT involves several key
        capabilities:
      </p>

      <h3>Pre-Built Clinical Prompt Templates</h3>

      <p>
        Rather than starting from a blank prompt every time, practitioners
        benefit from structured templates that are designed for each phase of
        the PAT arc. A preparation session template, for instance, would
        prompt for readiness indicators, contraindication screening outcomes,
        psychoeducation topics covered, alliance observations, and the
        client&rsquo;s stated intentions. A dosing session template would
        prompt for timeline of the experience, substance and dosage, observed
        and reported phenomena, emotional and somatic responses, key themes or
        imagery, and practitioner observations about the client&rsquo;s
        process. An integration template would prompt for material being
        processed, emerging insights, behavioural changes, and links back to
        presenting concerns and treatment goals.
      </p>

      <p>
        The MycenAI Clinical Prompt Library provides exactly this &mdash; a
        downloadable collection of templates designed specifically for PAT
        documentation across the full treatment arc.
      </p>

      <h3>Orientation-Aware Prompting</h3>

      <p>
        PAT practitioners come from diverse clinical backgrounds and bring
        different theoretical orientations to the work. A practitioner trained
        in Internal Family Systems will frame a dosing session very differently
        from one working within a somatic experiencing framework, a
        transpersonal psychology lens, or a cognitive-behavioural integration
        model. AI documentation tools need to adapt to these orientations
        rather than imposing a single clinical language.
      </p>

      <p>
        Orientation-aware prompting means that the same clinical material can
        be documented through the lens of IFS (tracking parts, exiles,
        protectors), somatic approaches (tracking body sensations, movement
        impulses, nervous system regulation), ACT (tracking psychological
        flexibility, values clarification, defusion), DBT (tracking distress
        tolerance, emotion regulation, interpersonal effectiveness), CBT
        (tracking cognitive distortions, behavioural activation, thought
        records), or psychodynamic and transpersonal frameworks (tracking
        unconscious material, archetypal imagery, spiritual emergence). The
        practitioner&rsquo;s orientation shapes how the AI structures the
        output, ensuring the documentation reflects the clinician&rsquo;s
        actual conceptualisation of the work.
      </p>

      <h3>Document Chaining Across the Treatment Arc</h3>

      <p>
        One of the most powerful applications of AI in PAT documentation is document chaining &mdash; a
        workflow where the output from one session becomes input context for
        the next. In PAT, this is not a convenience feature. It is a clinical
        necessity.
      </p>

      <p>
        The preparation sessions establish intentions, identify fears, and
        surface relevant history. That material needs to be available when
        documenting the dosing session, so the practitioner can note where
        preparation themes appeared, how intentions were engaged, and what
        unexpected material emerged. The dosing session record then needs to
        feed forward into integration, so the clinician can track how specific
        experiences are being processed, revisited, or avoided across
        subsequent sessions.
      </p>

      <p>
        Without document chaining, this continuity relies entirely on the
        practitioner&rsquo;s memory and manual cross-referencing. With it, the
        AI can surface relevant context from earlier phases and help the
        clinician maintain a coherent narrative across the entire episode of
        care.
      </p>

      <h2>The Privacy Imperative</h2>

      <p>
        Privacy is a foundational concern in all clinical documentation. In
        PAT, it is elevated to an imperative. There are several reasons for
        this.
      </p>

      <p>
        First, the legal landscape around psychedelic substances is uneven and
        evolving. In some jurisdictions, psilocybin therapy is legal or
        decriminalised. In others, possession of the substance remains a
        criminal offence regardless of the therapeutic context. Clients who
        participate in PAT &mdash; whether in clinical trials, state-approved
        programmes, or other legal frameworks &mdash; are disclosing their
        involvement with substances that carry legal risk. That disclosure
        must be protected with the highest available standard of security.
      </p>

      <p>
        Second, the clinical material that surfaces in PAT is often
        extraordinarily sensitive. Dosing sessions can bring forward deeply
        buried trauma, experiences of abuse, existential crises, and
        profoundly personal spiritual or psychological content. Clients in
        these states are exceptionally vulnerable, and the documentation of
        their experiences must be handled with corresponding care.
      </p>

      <p>
        Third, many PAT practitioners and their clients are, whether they
        recognise it or not, early participants in what will become a regulated
        industry. The documentation they create today may be subject to future
        regulatory scrutiny, and the security standards applied to that
        documentation will matter.
      </p>

      <p>
        This is why software-only encryption is not sufficient. Standard
        cloud-based AI tools process data in server memory that is accessible
        to the platform operator, system administrators, and potentially to
        legal discovery. Even if data is encrypted at rest and in transit,
        it is decrypted during processing &mdash; and that is the point of
        vulnerability.
      </p>

      <p>
        Hardware-secured processing &mdash; specifically, confidential
        computing using trusted execution environments (TEEs) &mdash;
        addresses this gap. TEEs encrypt data not just at rest and in transit,
        but{" "}
        <em>during processing</em>. The data is processed inside a
        hardware-isolated enclave that cannot be accessed by the cloud
        provider, the platform operator, or anyone other than the
        authenticated user. For a deeper exploration of how this technology
        evolved from physical record-keeping to modern hardware security,
        see From Filing Cabinets to Enclaves.
      </p>

      <p>
        For PAT practitioners, this is not an abstract infrastructure
        decision. It is a direct clinical and ethical obligation. If you are
        asking clients to disclose their use of legally complex substances
        and to share the most vulnerable content of their inner lives, the
        tools you use to document that material must offer protections that
        match the sensitivity of the disclosure. Understanding how HIPAA intersects with AI processing is an essential part of this equation.
      </p>

      <p>
        ConfideAI&rsquo;s infrastructure is built on this principle. Every AI
        interaction is processed within a hardware-secured enclave, ensuring
        that clinical data &mdash; including the uniquely sensitive material
        generated in PAT &mdash; is never exposed to any party other than the
        practitioner.
      </p>

      <h2>Where This Is Heading</h2>

      <p>
        The psychedelic-assisted therapy field is at an inflection point. The
        U.S. FDA has granted breakthrough therapy designation to psilocybin
        for treatment-resistant depression and to MDMA-assisted therapy for
        PTSD. Australia&rsquo;s TGA has approved the prescribing of psilocybin
        and MDMA by authorised psychiatrists. Regulatory frameworks are
        emerging in Canada, the United Kingdom, and across the European Union.
        State-level programmes in Oregon and Colorado are operationalising
        legal access to psilocybin outside of clinical trials.
      </p>

      <p>
        As these frameworks mature, the documentation standards applied to PAT
        will tighten. Practitioners who are currently operating with
        improvised documentation workflows &mdash; adapting standard therapy
        note templates, writing free-form session summaries, or relying on
        memory to connect preparation, dosing, and integration records &mdash;
        will need to adopt more structured, auditable, and secure systems.
      </p>

      <p>
        The tools need to match the maturity of the clinical work. PAT is no
        longer an experimental fringe. It is moving into mainstream clinical
        practice, and the documentation infrastructure must move with it. That
        means purpose-built templates that understand the phases of
        psychedelic therapy. It means orientation-aware AI that respects the
        diversity of clinical frameworks practitioners bring to this work. It
        means document chaining that maintains continuity across the full
        treatment arc. And it means privacy protections that are engineered at
        the hardware level, not bolted on as an afterthought.
      </p>

      <p>
        The practitioners entering this field now are shaping how PAT will be
        documented, regulated, and understood for decades to come. The
        documentation choices they make today &mdash; what tools they use, what
        standards they adopt, how they protect client data &mdash; will
        establish the foundation for an entire clinical discipline.
      </p>

      <hr />

      <h2>Getting Started</h2>

      <p>
        MycenAI provides AI-assisted clinical documentation tools designed
        specifically for psychedelic-assisted therapy practitioners, built on{" "}
        <a
          href="https://confideai.ai/welcome/mycenai"
          target="_blank"
          rel="noopener noreferrer"
        >
          ConfideAI&rsquo;s
        </a>{" "}
        hardware-secured infrastructure. The{" "}
        <strong>Clinical Prompt Library</strong> is available as a free
        download and includes structured templates for every phase of the PAT
        treatment arc &mdash; from intake screening through integration and
        discharge.
      </p>

      <p>
        Visit{" "}
        <a
          href="https://confideai.ai/welcome/mycenai"
          target="_blank"
          rel="noopener noreferrer"
        >
          confideai.ai
        </a>{" "}
        to access the platform, or download the Clinical Prompt Library to
        start building a documentation workflow that matches the standard of
        care your clients deserve.
      </p>
    </>
  );
};

export default AiInPat;
